import { useCallback, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useStringsContext } from '../../../../contexts/strings';
import { useAppConfig } from '../../../../hooks/appConfigs';
import { AppConfigData } from '../../../../types/app-config';
import {
  FotbollPlayAppConfig,
  FotbollPlayTabIndex,
  FotbollPlayTabPayloadMap,
  NotificationTabPayload,
  SettingsTabPayload,
  StartTabPayload,
  VideoCarouselsTabPayload,
} from './types';

function sanitizeFotbollPlayConfig(
  config: FotbollPlayAppConfig & { id?: string }
): FotbollPlayAppConfig {
  const sanitizedConfig = { ...config };
  delete sanitizedConfig.id;

  return sanitizedConfig;
}

function getConfigPatch(
  tab: FotbollPlayTabIndex,
  data: FotbollPlayTabPayloadMap[FotbollPlayTabIndex]
): Partial<FotbollPlayAppConfig> {
  switch (tab) {
    case 0: {
      const settingsData = data as SettingsTabPayload;
      return {
        settings: settingsData,
      };
    }
    case 1:
      return { start: data as StartTabPayload };
    case 2:
      return { videoCarousels: data as VideoCarouselsTabPayload };
    case 3:
      return { notifications: data as NotificationTabPayload };
    default:
      return {};
  }
}

export function useFotbollPlayApi(teamAbbrv: string) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { strings } = useStringsContext();
  const { appConfig, updateAppConfig, isUpdating } = useAppConfig(teamAbbrv);

  const submit = useCallback(
    async <T extends FotbollPlayTabIndex>(tab: T, data: FotbollPlayTabPayloadMap[T]) => {
      setIsSubmitting(true);

      try {
        const currentAppConfig = appConfig || ({} as AppConfigData);
        const currentConfig = currentAppConfig?.fotbollplay
          ? sanitizeFotbollPlayConfig(currentAppConfig.fotbollplay as FotbollPlayAppConfig)
          : {};
        const configPatch = getConfigPatch(tab, data);
        const nextConfig: FotbollPlayAppConfig = {
          ...currentConfig,
          ...configPatch,
        };

        await updateAppConfig({
          fotbollplay: nextConfig,
        });

        enqueueSnackbar(strings.messages.saveSuccess, { variant: 'success' });
      } catch (error) {
        console.error(error);
        enqueueSnackbar(strings.messages.saveError, { variant: 'error' });
        throw error;
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      appConfig,
      enqueueSnackbar,
      strings.messages.saveError,
      strings.messages.saveSuccess,
      updateAppConfig,
    ]
  );

  return {
    isSubmitting: isSubmitting || isUpdating,
    submit,
  };
}

export function useFotbollPlayConfig(teamAbbrv: string) {
  const { appConfig, loading, error } = useAppConfig(teamAbbrv);

  return [appConfig?.fotbollplay as FotbollPlayAppConfig | undefined, loading, error] as const;
}
